import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Search, Check } from "lucide-react";
import useCrmApi from "../../../components/hook/crm-req";
import useRequest from "../../../components/hook/use-req";

// Add New Deal — POST /deals per docs/deals-ui-integration.md, which must
// carry exactly one customer identity (contact_id or lead_id, never both).
// The picker here searches contacts via the same GET /contacts?search=
// endpoint the Customers list already uses; lead-linked deals aren't offered
// since there's no confirmed lead-search endpoint to back that picker yet.

interface ContactOption {
  id: string;
  first_name?: string;
  firstName?: string;
  last_name?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

interface TeamMember {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

const inputClass =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20";

const contactName = (c: ContactOption) => `${c.first_name || c.firstName || ""} ${c.last_name || c.lastName || ""}`.trim() || c.email || c.phone || c.id;

const AddDealModal: React.FC<{ isOpen: boolean; onClose: () => void; onCreated: () => void }> = ({ isOpen, onClose, onCreated }) => {
  const token = localStorage.getItem("authToken");
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactOption | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [productOrService, setProductOrService] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [assignedTo, setAssignedTo] = useState("");

  const { makeRequest: searchContacts } = useCrmApi("/contacts", "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: createDeal } = useCrmApi("/deals", "POST", { Authorization: `Bearer ${token}` });
  const { makeRequest: getTeamList } = useRequest("/profile/users", "GET", { Authorization: `Bearer ${token}` });

  useEffect(() => {
    if (!isOpen) return;
    setSearch("");
    setContacts([]);
    setSelectedContact(null);
    setTitle("");
    setProductOrService("");
    setAmount("");
    setCurrency("INR");
    setAssignedTo("");
    (async () => {
      try {
        const [response] = await getTeamList();
        if (Array.isArray(response)) setTeam(response);
        else if (Array.isArray(response?.users)) setTeam(response.users);
        else if (Array.isArray(response?.results)) setTeam(response.results);
      } catch {
        setTeam([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !search.trim()) {
      setContacts([]);
      return;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      const [response] = await searchContacts(undefined, { search: search.trim(), limit: 8 });
      const items: ContactOption[] = Array.isArray(response) ? response : Array.isArray(response?.results) ? response.results : [];
      setContacts(items);
      setSearching(false);
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, isOpen]);

  const handleSubmit = async () => {
    if (!selectedContact) {
      toast.error("Pick a customer for this deal.");
      return;
    }
    if (!title.trim()) {
      toast.error("Give this deal a title.");
      return;
    }
    setSubmitting(true);
    const [response, status] = await createDeal({
      contact_id: selectedContact.id,
      title: title.trim(),
      product_or_service: productOrService.trim() || null,
      amount: amount.trim() === "" ? null : amount.trim(),
      currency: currency || null,
      assigned_to_user_id: assignedTo || null,
    });
    setSubmitting(false);

    if (status >= 200 && status < 300) {
      toast.success("Deal created.");
      onCreated();
      onClose();
    } else {
      toast.error(response?.detail || response?.message || "Failed to create deal.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Add New Deal</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Customer</label>
            {selectedContact ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-indigo-200 dark:border-indigo-500/40 bg-indigo-50/60 dark:bg-indigo-500/10">
                <span className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{contactName(selectedContact)}</span>
                <button type="button" onClick={() => setSelectedContact(null)} className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline shrink-0">
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search customers by name, phone, or email"
                    className={`${inputClass} pl-8`}
                  />
                </div>
                {searching && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">Searching…</p>}
                {contacts.length > 0 && (
                  <div className="mt-1.5 max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                    {contacts.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedContact(c)}
                        className="flex items-center justify-between w-full px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <span className="truncate">
                          <span className="font-medium text-gray-800 dark:text-gray-100">{contactName(c)}</span>
                          {c.phone && <span className="text-gray-400 dark:text-gray-500 ml-2">{c.phone}</span>}
                        </span>
                        <Check size={14} className="text-transparent shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
                {!searching && search.trim() && contacts.length === 0 && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">No customers match "{search.trim()}".</p>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Annual facility management plan" className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Product / Service</label>
            <input value={productOrService} onChange={(e) => setProductOrService(e.target.value)} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Amount</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Currency</label>
              <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} className={inputClass} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Assigned To</label>
            <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className={inputClass}>
              <option value="">Unassigned</option>
              {team.map((m) => (
                <option key={m.id} value={m.id}>
                  {`${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email || m.id}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 px-5 py-4 border-t border-gray-100 dark:border-gray-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-lg shadow-sm"
          >
            {submitting ? "Creating…" : "Create Deal"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddDealModal;
